import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, ArrowRight, Eye, EyeOff, Sun, Moon, ArrowLeft, Mail, Gift } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_SUBTITLES = {
  register: "Create an account to save your progress",
  forgot: "Reset your password",
  login: "Sign in to continue your journey",
};
const MODE_SUBMIT_LABELS = { register: "Create Account", forgot: "Submit Request", login: "Sign In" };

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, register } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();

  // Capture ?ref= and read it from localStorage so we can show a banner
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref && typeof window !== "undefined") {
      localStorage.setItem("pendingReferralCode", ref.toUpperCase().trim());
    }
  }, [searchParams]);
  const pendingRef = typeof window !== "undefined" ? localStorage.getItem("pendingReferralCode") : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    if (mode === "forgot") {
      try {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setSuccess(data.message);
      } catch (err) {
        setError(err.response?.data?.detail || "Something went wrong");
      }
      setSubmitting(false);
      return;
    }

    let result;
    if (mode === "register") {
      result = await register(name, email, password);
    } else {
      result = await login(email, password);
    }
    setSubmitting(false);
    if (result.success) {
      navigate(searchParams.get("next") === "service" ? "/services" : "/quickstart");
    } else {
      setError(result.error);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  return (
    <div
      data-testid="login-page"
      className="flex min-h-screen items-center justify-center bg-[var(--c-bg)] px-4"
    >
      {/* Theme toggle - top right */}
      <button
        data-testid="login-dark-mode-toggle"
        onClick={toggle}
        className="fixed right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
        aria-label="Toggle dark mode"
      >
        {dark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-[var(--c-text)]" />}
      </button>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-24 items-center justify-center overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-white px-5 shadow-[4px_4px_0px_var(--c-shadow)]">
            <img src="/logo.png" alt="Texas LLC Guide Logo" className="h-20 w-auto object-contain" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[var(--c-text)] sm:text-4xl">
            Texas LLC Guide
          </h1>
          <p className="mt-2 text-center text-[var(--c-muted)]">
            {MODE_SUBTITLES[mode] || MODE_SUBTITLES.login}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-8 shadow-[6px_6px_0px_var(--c-shadow)]">
          {/* Referral banner */}
          {mode === "register" && pendingRef && (
            <div data-testid="referral-banner" className="mb-5 flex items-start gap-3 rounded-lg border-2 border-[#A3E6D5] bg-[#A3E6D5]/15 p-3">
              <Gift className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1E3A8A]" />
              <div>
                <p className="text-sm font-bold text-[var(--c-text)]">You&apos;ve got $5 off!</p>
                <p className="text-xs text-[var(--c-muted)]">A friend (code <span className="font-mono font-bold">{pendingRef}</span>) referred you. $5 will be deducted at checkout.</p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
                  Full Name
                </label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3 text-sm font-medium text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20"
                  placeholder="John Doe"
                />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
                Email
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3 text-sm font-medium text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20"
                placeholder="you@example.com"
              />
            </div>
            {mode !== "forgot" && (
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
                  Password
                </label>
                <div className="relative">
                  <input
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3 pr-12 text-sm font-medium text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20"
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--c-muted)] hover:text-[var(--c-text)]"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot password link - only in login mode */}
            {mode === "login" && (
              <div className="text-right">
                <button
                  data-testid="forgot-password-link"
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-xs font-semibold text-[#1E3A8A] underline-offset-4 hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="rounded-lg border-2 border-[#FF4D4D] bg-red-50 px-4 py-3 text-sm font-medium text-[#FF4D4D]">
                {error}
              </div>
            )}

            {success && (
              <div data-testid="auth-success" className="rounded-lg border-2 border-[#A3E6D5] bg-[#A3E6D5]/10 px-4 py-3 text-sm font-medium text-[var(--c-text)]">
                <div className="flex items-start gap-2">
                  <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1E3A8A]" />
                  <span>{success}</span>
                </div>
              </div>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3.5 font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_var(--c-shadow)] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? (
                "Please wait..."
              ) : (
                <>
                  {MODE_SUBMIT_LABELS[mode] || MODE_SUBMIT_LABELS.login}
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 space-y-2 text-center">
            {mode === "forgot" ? (
              <button
                data-testid="back-to-login"
                onClick={() => switchMode("login")}
                className="flex items-center justify-center gap-1.5 mx-auto text-sm font-semibold text-[#1E3A8A] underline-offset-4 hover:underline"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Sign In
              </button>
            ) : (
              <button
                data-testid="toggle-auth-mode"
                onClick={() => switchMode(mode === "login" ? "register" : "login")}
                className="text-sm font-semibold text-[#1E3A8A] underline-offset-4 hover:underline"
              >
                {mode === "register"
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Create one"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
